import React, { useEffect, useRef, useState } from 'react'
import { Space, Typography, Alert } from 'antd'
import { GroupNode } from '../types'
import { DEFAULT_CONFIG, ExpressionEditorConfig } from '../fields'
import { createGroup, createRaw, parseExpression, serializeExpression, validateExpression } from '../expression'
import ExpressionGroup from './ExpressionGroup'

const { Text } = Typography

export type JsExpressionEditorProps = {
  /** Current expression as a JS string (the antd Form field value). */
  value?: string
  /** Emits the re-serialized JS string whenever the editor changes. */
  onChange?: (value: string) => void
  /** Available fields and operators. Defaults to DEFAULT_CONFIG (see fields.ts). */
  config?: ExpressionEditorConfig
  showPreview?: boolean
}

/** Build an editable tree from a stored expression string. */
const buildTree = (value: string | undefined, config: ExpressionEditorConfig): GroupNode => {
  if (!value || !value.trim()) {
    // Empty starter group so the user can immediately add conditions.
    return { ...createGroup(config), children: [] }
  }
  try {
    return parseExpression(value, config)
  } catch {
    // Unparseable stored value: keep the text editable as a raw node instead of losing it.
    return { ...createGroup(config), children: [createRaw(value)] }
  }
}

/**
 * Visual boolean-expression editor adapted as an antd Form field. Stores the
 * serialized JS string as the field value; pair with the `expressionFieldRule`
 * validator so the form surfaces syntax errors.
 */
export function JsExpressionEditor({ value, onChange, config = DEFAULT_CONFIG, showPreview = true }: JsExpressionEditorProps) {
  const [tree, setTree] = useState<GroupNode>(() => buildTree(value, config))
  // Track the last value we emitted so external resets re-parse, but our own edits don't.
  const lastEmitted = useRef<string | undefined>(value)

  useEffect(() => {
    if (value === lastEmitted.current) return
    setTree(buildTree(value, config))
    lastEmitted.current = value
  }, [value, config])

  const handleChange = (next: GroupNode) => {
    setTree(next)
    const serialized = serializeExpression(next)
    lastEmitted.current = serialized
    if (onChange) onChange(serialized)
  }

  const output = serializeExpression(tree)
  const error = output ? validateExpression(output) : null
  const preview = () => (
    <div>
      <Text type='secondary' style={{ fontSize: 12, marginRight: 8 }}>
        Preview:
      </Text>
      <Text code copyable type={error ? 'danger' : undefined}>
        {output || '—'}
      </Text>
    </div>
  )

  return (
    <Space direction='vertical' size='small' style={{ width: '100%' }}>
      <ExpressionGroup node={tree} config={config} onChange={handleChange} isRoot />
      {showPreview && preview()}
      {error && <Alert type='error' showIcon message='Invalid expression' description={error} />}
    </Space>
  )
}
